import { useEffect, useState } from "react";

/**
 * Extracts the dominant color from a logo URL and returns a contrasting
 * background color. Sets crossOrigin before src to fix Safari/Edge tainted
 * canvas issues. Falls back to "#ffffff" on any failure.
 */
export function useLogoContrastBg(logoUrl?: string): string {
  const [bg, setBg] = useState("#ffffff");

  useEffect(() => {
    if (!logoUrl) {
      setBg("#ffffff");
      return;
    }

    let cancelled = false;
    const img = new Image();
    // crossOrigin MUST be set before src — Safari is strict about order
    img.crossOrigin = "anonymous";
    img.src = logoUrl;

    const onLoad = () => {
      if (cancelled) {
        return;
      }
      try {
        const canvas = document.createElement("canvas");
        // Sample a small region — enough for dominant color, fast
        canvas.width = 20;
        canvas.height = 20;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return setBg("#ffffff");
        }

        ctx.drawImage(img, 0, 0, 20, 20);
        const data = ctx.getImageData(0, 0, 20, 20).data;

        // Average all non-transparent pixels
        let r = 0,
          g = 0,
          b = 0,
          count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha > 10) {
            // skip near-transparent pixels
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count++;
          }
        }

        if (count === 0) {
          return setBg("#ffffff");
        }

        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);

        // WCAG relative luminance — pick white or dark for max contrast
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        setBg(luminance > 0.5 ? "#1a1a1a" : "#ffffff");
      } catch {
        // Canvas tainted or other error — safe fallback
        setBg("#ffffff");
      }
    };

    const onError = () => {
      if (!cancelled) {
        setBg("#ffffff");
      }
    };

    img.addEventListener("load", onLoad);
    img.addEventListener("error", onError);

    return () => {
      cancelled = true;
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
    };
  }, [logoUrl]);

  return bg;
}
